import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import Loader from "@components/Loader/Loader";
import { Reminder } from "@models/reminder";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  useCreateReminderMutation,
  useDeleteReminderMutation,
  useGetRemindersQuery,
  useUpdateReminderMutation,
} from "@store/reminderApi";
import ReminderDrawer from "@components/Home/Schedule/ReminderDrawer";
import ReminderList from "@components/ReminderList";
import { setMessage } from "@store/snackbarSlice";
import ScheduleToolbar from "@components/Home/Schedule/ScheduleToolbar";
import { Stack } from "@mui/material";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import { useParams } from "react-router-dom";
import PageWrapper from "@components/Home/PageWrapper";
import EmptyState from "@components/EmptyState/EmptyState";
import { useGetPlanSubscriptionDetailsQuery } from "@store/planApi";

const Schedule: FC = () => {
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: subscription } = useGetPlanSubscriptionDetailsQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );
  const maxReminders = ((subscription?.plan.limits.reminders ?? -1) >= 0 && subscription?.plan.limits.reminders) || 5;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<
    Reminder | undefined
  >();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [createReminderApi] = useCreateReminderMutation();
  const [updateReminderApi] = useUpdateReminderMutation();
  const [deleteReminderApi, { isLoading: isDeleting }] =
    useDeleteReminderMutation();
  const { data: reminders, isLoading } = useGetRemindersQuery(
    {
      patientId: selectedPatientId!,
    },
    {
      skip: !selectedPatientId,
    },
  );
  const [checkedReminderIds, setCheckedReminderIds] = useState<Set<string>>(
    new Set(),
  );
  const checkedEnabled = useMemo(
    () =>
      new Set(
        (reminders?.data || []).filter(
          ({ id, isActive }) => isActive && checkedReminderIds.has(id),
        ),
      ),
    [checkedReminderIds, reminders],
  );
  const checkedDisabled = useMemo(
    () =>
      new Set(
        (reminders?.data || []).filter(
          ({ id, isActive }) => !isActive && checkedReminderIds.has(id),
        ),
      ),
    [checkedReminderIds, reminders],
  );

  useEffect(() => {
    const id = params.id;
    if (id) {
      const reminder = reminders?.data?.find((r) => r.id === id);
      if (reminder) {
        setSelectedReminder(reminder);
        setReminderDrawerOpen(true);
      }
    }
  }, [params, reminders]);

  const createReminder = useCallback(
    async (reminder: Partial<Reminder>) => {
      const { error } = await createReminderApi(reminder);

      if (error) {
        dispatch(
          setMessage({
            content:
              (error as any).status === 429
                ? t(`errors.maxReminders`, { maxReminders })
                : t(`errors.unknown`),
            severity: "error",
          }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`schedule.saveSuccessMessage`),
            severity: "success",
          }),
        );
      }
    },
    [maxReminders, createReminderApi, dispatch, t],
  );

  const updateReminder = useCallback(
    async (reminder: Partial<Reminder> & { id: string }) => {
      const res = await updateReminderApi(reminder);
      if (res.error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`schedule.saveSuccessMessage`),
            severity: "success",
          }),
        );
      }
      return res;
    },
    [dispatch, t, updateReminderApi],
  );

  const deleteReminder = useCallback(
    async (reminderId: string) => {
      const { error } = await deleteReminderApi({ id: reminderId });
      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        setCheckedReminderIds((reminderIds) => {
          const newSet = new Set(reminderIds);
          newSet.delete(reminderId);
          return newSet;
        });
      }
    },
    [dispatch, t, deleteReminderApi],
  );

  const onEnabledChange = useCallback(
    (reminder: Reminder) => {
      return updateReminder({
        ...reminder,
        isActive: !reminder.isActive,
      });
    },
    [updateReminder],
  );

  const onEnabledChangeChecked = useCallback(
    (enable: boolean) => {
      const reminders = enable ? checkedDisabled : checkedEnabled;
      if (!reminders.size) {
        return;
      }
      reminders.forEach(async (reminder) => {
        const { error } = await onEnabledChange(reminder);
        if (!error) {
          setCheckedReminderIds((reminderIds) => {
            const newSet = new Set(reminderIds);
            newSet.delete(reminder.id);
            return newSet;
          });
        }
      });
    },
    [checkedEnabled, checkedDisabled, onEnabledChange],
  );

  const checkReminder = useCallback((reminder: Reminder) => {
    setCheckedReminderIds((reminderIds) => {
      const newSet = new Set(reminderIds);
      if (newSet.has(reminder.id)) {
        newSet.delete(reminder.id);
      } else {
        newSet.add(reminder.id);
      }
      return newSet;
    });
  }, []);

  const onEdit = useCallback((reminder: Reminder) => {
    setSelectedReminder(reminder);
    setReminderDrawerOpen(true);
  }, []);

  const onDrawerClose = useCallback(() => {
    setSelectedReminder(undefined);
    setReminderDrawerOpen(false);
  }, []);

  const onConfirmDelete = useCallback(async () => {
    setConfirmDeleteDialogOpen(false);
    if (!selectedReminder && !checkedReminderIds.size) {
      return;
    }
    if (selectedReminder) {
      await deleteReminder(selectedReminder.id);
      onDrawerClose();
    } else {
      checkedReminderIds.forEach((reminderId) => {
        deleteReminder(reminderId);
      });
    }
  }, [deleteReminder, checkedReminderIds, selectedReminder, onDrawerClose]);

  const onSave = useCallback(
    async (reminder: Omit<Reminder, "id" | "patientId">) => {
      if (selectedReminder) {
        await updateReminder({
          ...reminder,
          id: selectedReminder.id,
          patientId: selectedPatientId!,
        });
      } else {
        await createReminder({ ...reminder, patientId: selectedPatientId! });
      }
      onDrawerClose();
    },
    [
      selectedReminder,
      updateReminder,
      createReminder,
      selectedPatientId,
      onDrawerClose,
    ],
  );

  const onCreateReminder = () => {
    if (!reminders || reminders.data.length >= maxReminders) {
      dispatch(
        setMessage({
          content: t(`errors.maxReminders`, { maxReminders }),
          severity: "error",
        }),
      );
      return;
    }
    setReminderDrawerOpen(true);
  };

  return (
    <PageWrapper>
      <HomePageTitle title={t("schedule.title")} />
      {!reminders?.data || isLoading ? (
        <Loader />
      ) : (
        <Stack gap="32px">
          <ScheduleToolbar
            checkedEnabled={checkedEnabled.size}
            checkedDisabled={checkedDisabled.size}
            onCreate={onCreateReminder}
            onDelete={() => setConfirmDeleteDialogOpen(true)}
            onDisable={() => onEnabledChangeChecked(false)}
            onEnable={() => onEnabledChangeChecked(true)}
          />
          {reminders.data.length ? (
            <ReminderList
              reminders={reminders.data}
              onEdit={onEdit}
              onEnabledChange={onEnabledChange}
              onSelect={checkReminder}
              selectedReminderIds={checkedReminderIds}
            />
          ) : (
            <EmptyState
              image={`${process.env.PUBLIC_URL}/images/art-schedule2.jpeg`}
              label={t("schedule.noData")}
            />
          )}
        </Stack>
      )}
      <ReminderDrawer
        open={reminderDrawerOpen}
        reminder={selectedReminder}
        onClose={onDrawerClose}
        onDelete={() => setConfirmDeleteDialogOpen(true)}
        onSave={onSave}
      />

      <ConfirmDialog
        processing={isDeleting}
        open={confirmDeleteDialogOpen}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmDeleteDialogOpen(false)}
      ></ConfirmDialog>
    </PageWrapper>
  );
};

export default Schedule;
